import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, GitMerge, CheckCircle2, XCircle, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { StatusBadge, Loading, Select } from "../common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { fdate, fdatetime } from "../../lib/format";
import { REVIEW_LABEL, ScorePill, ProspectFields, assigneeOptions, assignPayload } from "./shared";

const Ext = ({ href }) => (href ? <a href={href.startsWith("http") ? href : `https://${href}`} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 break-all">{href}<ExternalLink className="h-3 w-3 shrink-0" /></a> : "—");

function Details({ p }) {
  const rows = [["Contact", p.contact_name], ["Category", p.category], ["Phone", p.phone], ["Email", p.email], ["Website", <Ext href={p.website} />],
    ["Business profile", <Ext href={p.profile_url} />], ["City / District", [p.city, p.district].filter(Boolean).join(" / ")], ["State / Country", [p.state, p.country].filter(Boolean).join(", ")],
    ["Source", p.source], ["Source URL", p.source_url?.startsWith("http") ? <Ext href={p.source_url} /> : p.source_url], ["First discovered", fdatetime(p.first_discovered_at)],
    ["Last checked", fdatetime(p.last_checked_at)], ["Last verified", p.last_verified_at ? fdatetime(p.last_verified_at) : "Not verified yet"], ["Times seen", p.times_seen],
    ["Follow-up", fdate(p.followup_date)], ["CRM lead", p.crm_lead_id ? <Link className="text-primary font-semibold" to={`/leads/${p.crm_lead_id}`}>{p.crm_lead_code}</Link> : "—"]];
  return (
    <dl className="grid grid-cols-[130px_1fr] gap-y-1.5 text-sm" data-testid="prospect-details">
      {rows.map(([k, v]) => <div key={k} className="contents"><dt className="text-xs text-muted-foreground">{k}</dt><dd className="font-medium min-w-0 break-words">{v || "—"}</dd></div>)}
      {p.social_urls?.length > 0 && <><dt className="text-xs text-muted-foreground">Social profiles</dt><dd className="space-y-0.5">{p.social_urls.map((u) => <div key={u}><Ext href={u} /></div>)}</dd></>}
    </dl>
  );
}

function Evidence({ p }) {
  return (
    <div data-testid="prospect-score-breakdown">
      <div className="flex items-center gap-2 mb-2"><span className="text-sm font-semibold">Score</span><ScorePill score={p.score} /><StatusBadge value={p.qualification} /></div>
      <ul className="space-y-1.5 text-xs">{p.score_breakdown.map((b) => (
        <li key={b.key} className="flex gap-2"><span className={`font-num font-bold w-12 shrink-0 ${b.points ? "text-emerald-600" : "text-slate-400"}`}>+{b.points}/{b.max}</span>
          <span><span className="font-semibold">{b.label}</span> — <span className="text-muted-foreground">{b.evidence}</span></span></li>
      ))}</ul>
      {p.website_check && <p className="text-[11px] text-muted-foreground mt-2">Website check {fdatetime(p.website_check.checked_at)}: {p.website_check.ok ? `reachable (pages: ${p.website_check.pages.length})` : p.website_check.error}</p>}
    </div>
  );
}

function Matches({ p, act }) {
  if (!p.flagged_matches?.length) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="prospect-flagged-matches">
      <div className="text-sm font-semibold text-amber-800 mb-2">Possible duplicate — review before approving</div>
      {p.flagged_matches.map((m) => (
        <div key={m.id} className="flex flex-wrap items-center gap-2 text-sm py-1">
          <span className="font-semibold">{m.type} {m.code}</span><span>{m.name}</span><span className="text-xs text-muted-foreground">{m.city} · {m.fields.join(", ")}{m.similarity != null ? ` · ${Math.round(m.similarity * 100)}% similar` : ""}</span>
          {!["approved", "merged"].includes(p.review_status) && <Button size="sm" variant="outline" className="ml-auto h-7" data-testid={`prospect-merge-${m.code}`} onClick={() => act("merge", { target_type: m.type === "client" ? "lead" : m.type, target_id: m.id })}><GitMerge className="h-3.5 w-3.5 mr-1" />Merge into this</Button>}
        </div>
      ))}
      {!["approved", "merged"].includes(p.review_status) && <Button size="sm" variant="ghost" className="h-7 mt-1" data-testid="prospect-not-duplicate" onClick={() => act("not-duplicate")}>Not a duplicate — keep separate</Button>}
    </div>
  );
}

function History({ p }) {
  return (
    <ol className="text-xs space-y-1.5 border-l-2 border-violet-100 pl-3" data-testid="prospect-history">
      {[...(p.history || [])].reverse().map((h) => (
        <li key={`${h.at}-${h.event}`}><span className="text-muted-foreground">{fdatetime(h.at)}</span> · <span className="font-semibold capitalize">{h.event.replace(/_/g, " ")}</span>
          {h.source ? ` · ${h.source}` : ""}{h.result ? ` · ${h.result}` : ""}{h.filled?.length ? ` · filled ${h.filled.join(", ")}` : ""}{h.lead_code ? ` · ${h.lead_code}` : ""}{h.by ? ` · by ${h.by}` : ""}</li>
      ))}
    </ol>
  );
}

export default function ProspectDialog({ id, onClose, onChanged, sales }) {
  const [p, setP] = useState(null);
  const [edit, setEdit] = useState(null);
  const [assignee, setAssignee] = useState("");
  const load = useCallback(() => {
    if (id) api.get(`/leadgen/prospects/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  useEffect(() => { setP(null); setEdit(null); setAssignee(""); load(); }, [load]);
  const act = async (path, body = {}, msg = "Saved") => {
    try { await api.post(`/leadgen/prospects/${id}/${path}`, body); toast.success(msg); onChanged(); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    try { const r = await api.put(`/leadgen/prospects/${id}`, edit); setP({ ...p, ...r.data }); setEdit(null); toast.success("Prospect updated & re-scored"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const open = ["pending", "below_threshold"].includes(p?.review_status);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="prospect-dialog">
        <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2">{p?.business_name || "Prospect"}{p && <><span className="text-xs text-muted-foreground font-normal">{p.code}</span><StatusBadge value={REVIEW_LABEL[p.review_status]} /><StatusBadge value={p.verification_status} /></>}</DialogTitle></DialogHeader>
        {!p ? <Loading /> : edit ? (
          <div className="space-y-3"><ProspectFields f={edit} setF={setEdit} prefix="prospect-edit" />
            <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button className="vmw-gradient" onClick={save} data-testid="prospect-edit-save">Save & re-score</Button></div></div>
        ) : (
          <div className="space-y-4">
            <Matches p={p} act={(path, body) => act(path, body, path === "merge" ? "Merged — original history preserved" : "Kept as separate business")} />
            <div className="grid md:grid-cols-2 gap-5"><Details p={p} /><Evidence p={p} /></div>
            {p.sales_notes && <p className="text-sm p-3 rounded-lg bg-muted/60 whitespace-pre-wrap">{p.sales_notes}</p>}
            {open && (
              <div className="vmw-card p-3 flex flex-wrap items-center gap-2" data-testid="prospect-actions">
                <Select testid="prospect-assignee" className="w-60" placeholder="Approve without assigning" options={assigneeOptions(sales)} value={assignee} onChange={setAssignee} />
                <Button className="vmw-gradient" data-testid="prospect-approve" onClick={() => act("approve", assignPayload(assignee), "Approved — added to CRM leads")}><CheckCircle2 className="h-4 w-4 mr-1" />Approve to CRM</Button>
                <Button variant="outline" data-testid="prospect-reject" onClick={() => act("reject", { reason: "" }, "Rejected")}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                <Button variant="outline" data-testid="prospect-edit" onClick={() => setEdit({ ...p })}><Pencil className="h-4 w-4 mr-1" />Edit / research</Button>
              </div>
            )}
            <div className="flex flex-wrap gap-2 items-center text-xs" data-testid="prospect-verify-actions">
              <ShieldCheck className="h-4 w-4 text-primary" /><span className="font-semibold">Manual verification:</span>
              {["Verified", "Needs Review", "Stale", "Invalid"].map((s) => <Button key={s} size="sm" variant="outline" className="h-7" data-testid={`prospect-verify-${s.toLowerCase().replace(" ", "-")}`} onClick={() => act("verify", { status: s }, `Marked ${s}`)}>{s}</Button>)}
            </div>
            <div><div className="text-sm font-semibold mb-2">Discovery & source history</div><History p={p} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
